import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { breakpoints } from 'styles/basic';

interface RowProps {
  type?: 'taka' | 'toru' | 'ryota' | 'tomoya' | 'alex' | 'yu';
}

interface ListProps {
  type?: 'leadVocals' | 'backingVocals' | 'leadGuitar' | 'rhythmGuitar' | 'addGuitars' | 'bass' | 'drums';
}

const leadVocals = '#37b3e6';
const backingVocals = 'rgba(0 0 0 / 0.4)';
const leadGuitar = '#4977d2';
const rhythmGuitar = '#bc2eab';
const addGuitars = '#cecc64';
const bass = '#369e9b';
const drums = '#7568e2';

const yearLength = '3rem';


const Wrapper = styled.div`
  margin-bottom: 2rem;
  font-size: 1.2rem;
`;

const ChartWrapper = styled.div`
  overflow: hidden;
  width: 100%;
`;

const Chart = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: .5rem;
  width: calc(100% - 7rem);
  margin-bottom: 4rem;
  margin-left: 6rem;
  padding: .5rem 0;
  border-bottom: .1rem solid #797676;

  @media (min-width: ${breakpoints.landscapePhone}) {
    width: 30.1rem;
  }
`;

const Row = styled.div<RowProps>`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 1.5rem;
  line-height: 1.4rem;
`;

const Name = styled.div`
  width: 6rem;
  margin-right: 1rem;
  margin-left: calc(-6rem - 1rem);
  text-align: right;
`;

const TimelineRow = styled.div<RowProps>`
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: .4rem;
    right: 0;
    left: 0;
    display: block;
    height: .7rem;
  }
  &::after {
    top: .4rem;
    height: .7rem;
  }

  ${(props) => props.type === 'taka' && css`
    width: 100%;
    background: ${leadVocals};
    &::before {
      left: calc(4 * ${yearLength});
      background: ${addGuitars};
    }
  `};
  ${(props) => props.type === 'toru' && css`
    width: 100%;
    background: ${rhythmGuitar};

    &::before {
      top: 0;
      left: calc(4.3 * ${yearLength});
      height: 100%;
      background: ${leadGuitar};
    }
    &::after {
      left: 0;
      background: ${backingVocals};
    }
  `};
  ${(props) => props.type === 'alex' && css`
    width: calc(4.3 * ${yearLength});
    background: ${leadGuitar};
    &::after {
      left: 0;
      background: ${backingVocals};
    }
  `};
  ${(props) => props.type === 'ryota' && css`
    width: 100%;
    background: ${bass};
    &::before {
      left: 0;
      background: ${backingVocals};
    }
  `};
  ${(props) => props.type === 'yu' && css`
    width: calc(2 * ${yearLength} + .1rem);
    background: ${drums};
  `};
  ${(props) => props.type === 'tomoya' && css`
    width: calc(100% - (2 * ${yearLength}));
    margin-left: calc(2 * ${yearLength});
    background: ${drums};
    &::before {
      left: 0;
      background: ${backingVocals};
    }
  `};
`;

const Timeline = styled.div`
  position: absolute;
  right: -1.4rem;
  bottom: -2.5rem;
  left: -1.5rem;
  display: flex;
`;

const Year = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 3rem;
  font-size: 1rem;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    bottom: 2.5rem;
    left: 50%;
    z-index: 1;
    height: calc(6 * (1.5rem + .5rem) + .5rem);
    border-left: 1px dashed #dadada;
  }
  &:first-of-type {
    &::before {
      z-index: 3;
      border-left: 1px solid #797676;
    }
  }
  &:last-of-type {
    position: absolute;
    right: 0;
  }

  @media (min-width: ${breakpoints.landscapePhone}) {
    font-size: 1.2rem;
  }
`;

const LegendList = styled.ul`
  max-width: 34rem;
  margin: 0 0 0 6rem;
  list-style: none;

  @media (min-width: ${breakpoints.landscapePhone}) {
    columns: 2;
  }
`;

const LegendListItem = styled.li<ListProps>`
  display: flex;
  align-items: flex-start;
  gap: .5rem;
  margin-bottom: .5rem;
  line-height: 1.2;

  &::before {
    content: '';
    display: block;
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
    margin-top: .2rem;
    border-radius: 50%;
    background: ${(props) => props.theme.color.gray};

    ${(props) => props.type === 'leadVocals' && css`
      background: ${leadVocals};
    `};
    ${(props) => props.type === 'leadGuitar' && css`
      background: ${leadGuitar};
    `};
    ${(props) => props.type === 'backingVocals' && css`
      background: ${backingVocals};
    `};
    ${(props) => props.type === 'rhythmGuitar' && css`
      background: ${rhythmGuitar};
    `};
    ${(props) => props.type === 'addGuitars' && css`
      background: ${addGuitars};
    `};
    ${(props) => props.type === 'bass' && css`
      background: ${bass};
    `};
    ${(props) => props.type === 'drums' && css`
      background: ${drums};
    `};
  }
`;

export {
  Wrapper,
  ChartWrapper,
  Chart,
  Row,
  Name,
  TimelineRow,
  Timeline,
  Year,
  LegendList,
  LegendListItem
};
